import React, { useEffect, useMemo, useState } from 'react';

/**
 * Performs the today operation.
 *
 * @returns {void} No value; the operation applies its documented side effects.
 */
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Performs the ingredient text operation.
 *
 * @param {*} ingredient - ingredient value.
 * @returns {*} The derived operation result.
 */
function ingredientText(ingredient) {
  return [ingredient.quantity, ingredient.unit, ingredient.item].filter(Boolean).join(' ');
}

/**
 * Renders the cooking session modal experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.session - session value.
 * @param {*} props.onSave - Callback invoked when save occurs.
 * @param {*} props.onFinish - Callback invoked when finish occurs.
 * @param {*} props.onDiscard - Callback invoked when discard occurs.
 * @param {*} props.onClose - Callback invoked when close occurs.
 * @returns {JSX.Element} The rendered interface.
 */
export default function CookingSessionModal({ session: initialSession, onSave, onFinish, onDiscard, onClose }) {
  const [session, setSession] = useState(initialSession);
  const [showFinish, setShowFinish] = useState(false);
  const [showIncomplete, setShowIncomplete] = useState(false);
  const [showIngredients, setShowIngredients] = useState(false);
  const [cookedDate, setCookedDate] = useState(today());
  const [completionNote, setCompletionNote] = useState(initialSession.notes || '');
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState('');
  const [clock, setClock] = useState(Date.now());

  useEffect(() => { setSession(initialSession); }, [initialSession]);
  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const progress = useMemo(() => {
    const total = session.instructionsSnapshot.length;
    const complete = session.completedInstructionIds.length;
    return total ? Math.round((complete / total) * 100) : 0;
  }, [session]);

  const elapsed = useMemo(() => {
    const started = Date.parse(session.startedAt || '');
    if (!Number.isFinite(started)) return 'In progress';
    const minutes = Math.max(Math.floor((clock - started) / 60000), 0);
    return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} hr ${minutes % 60} min`;
  }, [clock, session.startedAt]);

  /**
   * Updates the requested operation.
   *
   * @param {*} next - next value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const update = async (next) => {
    setSession(next);
    setError('');
    try {
      const saved = await onSave(next);
      if (saved) setSession(saved);
    } catch (saveError) {
      setError(saveError.message || 'We could not save your progress.');
    }
  };

  /**
   * Performs the requested operation.
   *
   * @param {string} field - Field associated with the operation.
   * @param {string} id - Stable identifier of the target resource.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const toggle = (field, id) => {
    const values = session[field] || [];
    const nextValues = values.includes(id) ? values.filter((value) => value !== id) : [...values, id];
    update({ ...session, [field]: nextValues, ...(field === 'completedInstructionIds' ? { currentInstructionId: id } : {}) });
  };

  /**
   * Performs the requested operation.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const finish = async () => {
    setIsWorking(true);
    setError('');
    try {
      await onSave(session);
      await onFinish({ cookedDate, notes: completionNote });
    } catch (finishError) {
      setError(finishError.message || 'We could not finish this cooking session.');
      setIsWorking(false);
    }
  };

  /**
   * Removes the requested operation.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const discard = async () => {
    if (!window.confirm('Discard cooking? Your recipe will stay in your library.')) return;
    setIsWorking(true);
    try { await onDiscard(); } catch (discardError) {
      setError(discardError.message || 'We could not discard this cooking session.');
      setIsWorking(false);
    }
  };

  return (
    <div className="cooking-session-overlay" role="dialog" aria-modal="true" aria-label="Cooking mode">
      <div className="cooking-session-shell">
        <header className="cooking-session-header">
          <div><p className="eyebrow">Cooking · {elapsed}</p><h2>{session.recipeNameSnapshot}</h2></div>
          <button type="button" className="btn-ghost" onClick={onClose}>Close</button>
        </header>

        <div className="cooking-session-progress"><span style={{ width: `${progress}%` }} /></div>

        {showFinish ? (
          <main className="cooking-session-content compact">
            <section className="recipe-detail-panel">
              <h3>Finish cooking</h3>
              <label className="form-group">Cooked on<input type="date" max={today()} value={cookedDate} onChange={(event) => setCookedDate(event.target.value)} /></label>
              <label className="form-group"><span>Notes for next time</span><textarea rows="4" placeholder="What would you change next time? (Optional)" value={completionNote} onChange={(event) => setCompletionNote(event.target.value)} /></label>
            </section>
          </main>
        ) : (
          <main className="cooking-session-content">
            <section className="recipe-detail-panel cooking-session-servings">
              <h3>Servings</h3>
              <div className="session-stepper">
                <button type="button" aria-label="Decrease servings" onClick={() => update({ ...session, servingsMultiplier: Math.max(.25, session.servingsMultiplier - .25) })}>−</button>
                <strong>{session.servingsMultiplier}x</strong>
                <button type="button" aria-label="Increase servings" onClick={() => update({ ...session, servingsMultiplier: Math.min(8, session.servingsMultiplier + .25) })}>+</button>
              </div>
            </section>

            <button type="button" className="cooking-ingredients-trigger" onClick={() => setShowIngredients(true)}><span className="cooking-ingredients-trigger-icon">☷</span><span><strong>View ingredients</strong><small>{session.ingredientsSnapshot.length} {session.ingredientsSnapshot.length === 1 ? 'ingredient' : 'ingredients'}</small></span><b>›</b></button>

            <section className="recipe-detail-panel"><h3>Instructions</h3><div className="session-checklist instructions">
              {session.instructionsSnapshot.map((step, index) => {
                const id = step.id || `step-${index}`;
                const checked = session.completedInstructionIds.includes(id);
                return <button type="button" className={checked ? 'complete' : ''} key={id} onClick={() => toggle('completedInstructionIds', id)}><span>{checked ? '✓' : index + 1}</span>{step.text}</button>;
              })}
            </div></section>

          </main>
        )}

        {error && <p className="field-error cooking-session-error">{error}</p>}
        <footer className="cooking-session-footer">
          {showFinish ? <>
            <button type="button" className="btn-secondary" onClick={() => setShowFinish(false)}>Back</button>
            <button type="button" className="btn-primary" onClick={finish} disabled={isWorking}>{isWorking ? 'Saving...' : 'Finish'}</button>
          </> : <>
            <button type="button" className="btn-ghost danger-choice" onClick={discard} disabled={isWorking}>Discard cooking</button>
            <button type="button" className="btn-primary" onClick={() => {
              if (session.instructionsSnapshot.length > session.completedInstructionIds.length) setShowIncomplete(true);
              else setShowFinish(true);
            }}>Finish cooking</button>
          </>}
        </footer>
        {showIngredients && <div className="cooking-ingredients-sheet-backdrop" onMouseDown={() => setShowIngredients(false)}><section className="cooking-ingredients-sheet" role="dialog" aria-modal="true" aria-label="Ingredients" onMouseDown={(event) => event.stopPropagation()}><header><div><p className="eyebrow">Cooking</p><h3>Ingredients</h3></div><button type="button" className="btn-ghost" onClick={() => setShowIngredients(false)}>Done</button></header><div className="cooking-ingredients-sheet-meta">{session.servingsMultiplier}x servings</div><div className="cooking-ingredient-list">{session.ingredientsSnapshot.map((ingredient, index) => <div key={ingredient.id || `ingredient-${index}`}><i /> <span>{ingredientText(ingredient)}</span></div>)}</div></section></div>}
        {showIncomplete && <div className="modal-backdrop" onMouseDown={() => setShowIncomplete(false)}><section className="modal-card delete-confirmation" role="alertdialog" aria-modal="true" onMouseDown={(event) => event.stopPropagation()}><h2>Finish cooking?</h2><p>{session.instructionsSnapshot.length - session.completedInstructionIds.length} {session.instructionsSnapshot.length - session.completedInstructionIds.length === 1 ? 'step is' : 'steps are'} still unchecked.</p><div className="modal-actions"><button type="button" className="btn-primary" onClick={() => setShowIncomplete(false)}>Keep cooking</button><button type="button" className="btn-secondary" onClick={() => { setShowIncomplete(false); setShowFinish(true); }}>Finish anyway</button></div></section></div>}
      </div>
    </div>
  );
}
